import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { getTranslation } from "./translations/translations";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import FeaturedSection from "./components/FeaturedSection";
import MenuSection from "./components/MenuSection";
import AboutSection from "./components/AboutSection";
import DeliverySection from "./components/DeliverySection";
import Footer from "./components/Footer";
import CartSheet from "./components/CartSheet";
import CheckoutModal from "./components/CheckoutModal";
import OrderSuccess from "./components/OrderSuccess";
import OrderCancelled from "./components/OrderCancelled";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsAndConditions from "./components/TermsAndConditions";
import CompanyDetails from "./components/CompanyDetails";
import Allergens from "./components/Allergens";
import LocationSelector from "./components/LocationSelector";
import ProfilePage from "./components/ProfilePage";
import MikadoClosed from "./components/MikadoClosed";
import { Toaster } from "./components/ui/toaster";
import { toast } from "./hooks/use-toast";
import {
  featuredSushi,
  menuCategories,
  deliveryInfo,
  addToCart as addToCartMock,
  removeFromCart as removeFromCartMock,
  updateCartQuantity as updateCartQuantityMock,
  getCartTotal,
  loadCart,
  clearCart as clearCartMock
} from "./mock/mockData";

const Home = () => {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  useEffect(() => {
    setCart(loadCart());
  }, []);

  const handleAddToCart = (item) => {
    const updatedCart = addToCartMock(item);
    setCart([...updatedCart]);
    toast({
      title: t('addedToCart'),
      description: `${item.name} ${t('itemAdded')}`,
      duration: 2000
    });
  };

  const handleUpdateQuantity = (itemId, quantity) => {
    const updatedCart = updateCartQuantityMock(itemId, quantity);
    setCart([...updatedCart]);
  };

  const handleRemoveItem = (itemId) => {
    const updatedCart = removeFromCartMock(itemId);
    setCart([...updatedCart]);
    toast({
      title: t('itemRemoved'),
      description: t('itemRemovedDesc'),
      duration: 2000
    });
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    setIsCheckoutOpen(true);
  };

  const handleConfirmOrder = (formData) => {
    // Handle Cash on Delivery orders
    console.log('Cash order placed:', { formData, cart, total: getCartTotal() });
    
    toast({
      title: t('orderSuccess'),
      description: `Your order will arrive in ${deliveryInfo.estimatedTime}. Please prepare ${total.toFixed(2)} EUR in cash.`,
      duration: 5000
    });

    // Clear cart
    const clearedCart = clearCartMock();
    setCart([...clearedCart]);
    setIsCheckoutOpen(false);
  };

  const handleOrderClick = () => {
    const menuSection = document.getElementById('menu');
    if (menuSection) {
      menuSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);
  const subtotal = cart.reduce((total, item) => total + item.price * item.quantity, 0);
  const deliveryFee = subtotal >= deliveryInfo.freeDeliveryOver ? 0 : deliveryInfo.deliveryFee;
  const total = subtotal + deliveryFee;

  return (
    <div className="min-h-screen">
      <Header
        cartItemCount={cartItemCount}
        onCartClick={() => setIsCartOpen(true)}
        onOrderClick={handleOrderClick}
      />
      <HeroSection onOrderClick={handleOrderClick} />
      <MenuSection categories={menuCategories} onAddToCart={handleAddToCart} />
      <AboutSection />
      <DeliverySection deliveryInfo={deliveryInfo} />
      <Footer />
      
      <CartSheet
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onCheckout={handleCheckout}
        deliveryInfo={deliveryInfo}
      />

      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        cart={cart}
        total={total}
        onConfirmOrder={handleConfirmOrder}
      />

      <Toaster />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LocationSelector />} />
            <Route path="/home" element={<Home />} />
            <Route path="/mikado" element={<MikadoClosed />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/order-success" element={<OrderSuccess />} />
            <Route path="/order-cancelled" element={<OrderCancelled />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/company-details" element={<CompanyDetails />} />
            <Route path="/allergens" element={<Allergens />} />
          </Routes>
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
